import { useCallback, useEffect, useState } from 'react';
import type { DashboardFrameworkCard, DashboardSummary } from '@mvcp/contracts';
import { getDashboard } from '../api';
// One owner for the band vocabulary and the Framework card (Story 8.1). This view
// used to carry a `BandPill` WITHOUT the `label`/`name` overrides the other copies
// had — the shared signature is the union of all three.
import { FrameworkCard } from './bands';

type DirState =
  | { kind: 'loading' }
  | { kind: 'ready'; summary: DashboardSummary }
  | { kind: 'error'; message: string };

/**
 * Compliance Manager cockpit → Compliance Directory hub (Story 4.3, FR-15/FR-16).
 * Lists all applicable Frameworks grouped by scope (Company-level vs Device-level),
 * each a wholly-clickable card that navigates to that Framework's detail view.
 * Reuses `getDashboard()` — `DashboardSummary.frameworks` already carries every
 * applicable Framework's `{frameworkId, code, name, scope, score}`, so the hub adds
 * no backend and its bands stay identical to the Dashboard's. Maritime-only
 * Frameworks are absent for a non-maritime Company (hidden, not empty).
 */
export function ComplianceDirectory({
  onOpenFramework,
}: {
  onOpenFramework: (frameworkId: string) => void;
}): React.JSX.Element {
  const [state, setState] = useState<DirState>({ kind: 'loading' });

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const summary = await getDashboard();
      setState({ kind: 'ready', summary });
    } catch (err: unknown) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : 'Failed to load' });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const company =
    state.kind === 'ready' ? state.summary.frameworks.filter((f) => f.scope === 'company') : [];
  const device =
    state.kind === 'ready' ? state.summary.frameworks.filter((f) => f.scope === 'device') : [];

  return (
    <>
      <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>
        Compliance Directory
      </h1>
      <p className="muted" style={{ margin: 0 }}>
        The regulations that apply to your organization. Open one to see it across the fleet.
      </p>

      {state.kind === 'loading' && (
        <section className="card stack" aria-busy="true" aria-label="Loading compliance directory">
          <p className="muted" style={{ margin: 0 }}>Loading…</p>
        </section>
      )}

      {state.kind === 'error' && (
        <section className="card stack" role="alert">
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Couldn’t load the directory</h2>
          <p className="muted" style={{ margin: 0 }}>{state.message}</p>
          <button
            type="button"
            className="btn-accent"
            data-surface="accent"
            onClick={() => void load()}
            style={{ alignSelf: 'flex-start' }}
          >
            Retry
          </button>
        </section>
      )}

      {state.kind === 'ready' && company.length === 0 && device.length === 0 && (
        <section className="card stack">
          <p className="muted" style={{ margin: 0 }}>No frameworks apply to your organization yet.</p>
        </section>
      )}

      {state.kind === 'ready' && company.length > 0 && (
        <FrameworkGroup title="Company-level" frameworks={company} onOpenFramework={onOpenFramework} />
      )}
      {state.kind === 'ready' && device.length > 0 && (
        <FrameworkGroup title="Device-level" frameworks={device} onOpenFramework={onOpenFramework} />
      )}
    </>
  );
}

function FrameworkGroup({
  title,
  frameworks,
  onOpenFramework,
}: {
  title: string;
  frameworks: DashboardFrameworkCard[];
  onOpenFramework: (frameworkId: string) => void;
}): React.JSX.Element {
  const headingId = `dir-${title.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <section className="card stack" aria-labelledby={headingId}>
      <h2 id={headingId} style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>{title}</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
        {frameworks.map((f) => (
          <FrameworkCard
            key={f.frameworkId}
            code={f.code}
            name={f.name}
            score={f.score}
            onOpen={() => onOpenFramework(f.frameworkId)}
          />
        ))}
      </div>
    </section>
  );
}
